import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2, Keyboard, Languages, Lock, MonitorPlay, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { btnGold, btnGhost } from "@/components/attendance/ui";

const FEATURES = [
  [BookOpen, "Hymns", "The CAC English & Yoruba hymn library with original numbers, plus your denomination's hymn numbers."],
  [Search, "Bible projection", "The complete World English Bible. Type John 3:16 or Psalm 23 — long passages split into readable slides."],
  [MonitorPlay, "Church announcements", "Professional templates with flyers, dates and locations — show one or rotate several automatically."],
  [Keyboard, "Choir special numbers", "Upload the choir's PDF or Word lyrics, tidy the verses and project them. Private to your church."],
  [Languages, "One service programme", "Plan the whole service — hymns, readings, notices, specials and prayer moments — and step through it."],
  [Lock, "View and project only", "Hymns are for projection during worship — no downloads, exports or copying."],
];

export default function ProjectionLanding() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [hasOrg, setHasOrg] = useState(false);
  useEffect(() => { api.get("/projection/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) api.get("/projection/me").then(() => setHasOrg(true)).catch(() => setHasOrg(false)); }, [user]);
  const primary = hasOrg || user?.role === "admin" ? ["/projection/console", "Open presenter console"] : ["/projection/register", `Start ${meta?.trial_days || 30}-day free trial`];

  return (
    <div data-testid="projection-landing">
      <section className="relative overflow-hidden border-b border-white/5">
        <img src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?crop=entropy&cs=srgb&fm=jpg&q=75&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 py-24 lg:py-32 mkt-rise">
          <div className="label-eyebrow mb-5">HOH Worship Projection</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.03] max-w-3xl">
            Everything your church needs <span className="italic text-[#D4AF37]">on one screen.</span>
          </h1>
          <p className="mt-6 text-neutral-300 text-base lg:text-lg max-w-2xl leading-relaxed">
            A professional, affordable church presentation platform for projecting hymns, Bible readings, church announcements and choir special numbers.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={primary[0]} data-testid="proj-landing-primary" className={`${btnGold} px-7 py-3.5`}>{primary[1]} <ArrowRight size={15} /></Link>
            {!user && <Link to="/login?next=/projection/console" data-testid="proj-landing-signin" className={`${btnGhost} px-7 py-3.5`}>Sign in</Link>}
          </div>
        </div>
      </section>
      <section className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map(([Icon, t, d]) => (
          <div key={t} className="luxury-card rounded-xl p-6">
            <Icon className="text-[#D4AF37]" size={22} />
            <h3 className="font-serif-display text-2xl text-white mt-4">{t}</h3>
            <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>
      <HowItWorks />
      {meta?.plans?.length > 0 && <Pricing meta={meta} cta={primary} />}
    </div>
  );
}

function HowItWorks() {
  const steps = ["Connect your laptop to the projector or TV by HDMI.", "Open the presenter console and click “Open audience display”.", "Move the display window to the big screen and go full screen.", "Search a hymn and step through the verses with your keyboard or clicker."];
  return (
    <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pb-20" data-testid="proj-how">
      <div className="label-eyebrow mb-3">How it works</div>
      <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <li key={s} className="border-l border-[#D4AF37]/40 pl-4">
            <div className="font-serif-display text-4xl text-[#D4AF37]">{i + 1}</div>
            <p className="text-sm text-neutral-300 mt-1 leading-relaxed">{s}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Pricing({ meta, cta }) {
  return (
    <section className="border-t border-white/5 bg-[#0D0D0D]" data-testid="proj-pricing">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20">
        <div className="label-eyebrow mb-3">Simple pricing</div>
        <h2 className="font-serif-display text-3xl lg:text-5xl text-white">Start with a <span className="italic text-[#D4AF37]">{meta.trial_days}-day free trial.</span></h2>
        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {meta.plans.map((p) => (
            <div key={p.id} className={`luxury-card rounded-2xl p-8 ${p.id === "church_bundle" ? "ring-1 ring-[#D4AF37]/50" : ""}`} data-testid={`proj-plan-${p.id}`}>
              <div className="text-xs tracking-widest uppercase text-neutral-400">{p.name}</div>
              <div className="flex items-end gap-2 mt-3"><span className="font-serif-display text-5xl text-white" data-testid={`proj-plan-${p.id}-monthly`}>£{p.price_monthly.toFixed(2)}</span><span className="text-neutral-400 mb-2">/ month</span></div>
              <div className="text-sm text-neutral-400 mt-1">or £{p.price_annual.toFixed(2)} / year</div>
              <ul className="mt-5 space-y-2 text-sm text-neutral-300">
                {(p.id === "church_bundle" ? ["HOH Worship Projection — everything included", "HOH Attendance live headcounts & Excel reports", "One subscription — never charged twice"] : ["HOH hymns projection", "Bible, announcements & choir specials", "Presenter console + audience display"]).map((x) => (
                  <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-[#D4AF37] shrink-0 mt-0.5" /> {x}</li>
                ))}
              </ul>
              <Link to={cta[0]} className={`${btnGold} w-full mt-6 py-3.5`} data-testid={`proj-plan-${p.id}-cta`}>{cta[1]}</Link>
            </div>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-6 max-w-3xl leading-relaxed">Your card is collected at sign-up. You won't be charged during the {meta.trial_days}-day trial; the first payment is taken on day {meta.trial_days + 1} unless you cancel. Renews automatically at the price shown. Already subscribed to HOH Attendance? Upgrade to the Bundle from your console — the difference is prorated.</p>
      </div>
    </section>
  );
}
